import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { useCreateComment } from 'services/help-desk/hooks';

interface DeleteTicketDialogProps {
  open: boolean;
  onClose: () => void;
}

function ClosingTicketDialog({ open, onClose }: DeleteTicketDialogProps) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { ticketId } = useParams<{ ticketId: string }>();

  const { mutate: createComment, isPending: isLoading } = useCreateComment({
    onSuccess: () => {
      if (ticketId) {
        queryClient.refetchQueries({
          queryKey: ['help-desk-details', ticketId],
        });
      }
      onClose();
    },
  });

  const closedTicket = () => {
    if (ticketId) {
      const params = {
        ticket_id: ticketId,
      };
      const formData = new FormData();
      formData.append('status', 'closed');
      createComment({ params: params, data: formData });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className='w-auto max-w-md p-4 sm:p-10 rounded-xl'
        onOpenAutoFocus={(e) => e.preventDefault()}
        hideClose
      >
        <DialogHeader>
          <DialogTitle className='text-lg text-center'>
            {t('are_you_sure')}
          </DialogTitle>
          <DialogDescription className='text-base text-center'>
            {t('ticket_delete_confirmation')}
          </DialogDescription>
        </DialogHeader>

        <div className='flex flex-col items-center justify-center h-auto'>
          <div className='flex items-center justify-start gap-3 mt-2'>
            <Button
              size='sm'
              variant='outline'
              className='px-8 border-primary text-primary hover:bg-primary/10'
              onClick={onClose}
            >
              {t('cancel')}
            </Button>

            <Button size='sm' className='px-8' onClick={closedTicket}>
              {isLoading && (
                <Loader2 role='progressbar' className='w-4 h-4 animate-spin' />
              )}
              {t('confirm')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ClosingTicketDialog;
